import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { Task } from '../../../../../../lib/db/models'
import type { TaskCreationData } from '../../../../components/TaskEntry'
import type { MonthBucket } from '../../hooks/upcomingTasks.utils'
import { DayGroup } from '../DayGroup'
import { MonthTitle } from '../MonthTitle'
import { DecorativeWave } from '../../../../../../components/ui/DecorativeWave'
import './MonthBlock.scss'

interface MonthBlockProps {
  bucket: MonthBucket
  /** Register this block's anchor ref under `bucket.monthKey` in the parent map. */
  registerAnchor: (monthKey: string, el: HTMLElement | null) => void
  /** Day-key of today (midnight-refreshed upstream). */
  todayDateKey: string
  /** Page-wide editing-task-id. The block narrows it per day so days without
   *  the edited task hit their React.memo short-circuit. */
  editingTaskId: string | null
  selectedTaskId: string | null
  /** Page-wide day-key currently in "create new task" mode. */
  creatingDateKey: string | null
  onStartEdit: (taskId: string) => void
  onCancelEdit: () => void
  onNavigateEdit: (taskId: string, direction: 'next' | 'prev') => void
  onSelectTask: (taskId: string) => void
  onStartCreate: (dateKey: string) => void
  onCancelCreate: () => void
  onSubmitCreate: (dateKey: string, data: TaskCreationData) => void
  onToggleComplete: (taskId: string) => void
  onDelete: (taskId: string) => void
  onUpdateTask: (taskId: string, data: TaskCreationData) => void
  onDuplicate?: (taskId: string) => void
  onContextMenuBadgeUpdate?: (taskId: string, updates: Partial<Omit<Task, 'id'>>) => void
}

const MonthBlockComponent: React.FC<MonthBlockProps> = ({
  bucket,
  registerAnchor,
  todayDateKey,
  editingTaskId,
  selectedTaskId,
  creatingDateKey,
  onStartEdit,
  onCancelEdit,
  onNavigateEdit,
  onSelectTask,
  onStartCreate,
  onCancelCreate,
  onSubmitCreate,
  onToggleComplete,
  onDelete,
  onUpdateTask,
  onDuplicate,
  onContextMenuBadgeUpdate,
}) => {
  const { t } = useTranslation()

  // Register the full <section> element as the anchor so the IO + overlap
  // tracker in useVisibleMonth measures the month's real visible height.
  // Ref callback (not useEffect) guarantees registration lands inside the
  // same commit that renders the block — the parent's initial-scroll
  // useLayoutEffect depends on this.
  const setAnchor = useCallback((el: HTMLElement | null) => {
    registerAnchor(bucket.monthKey, el)
  }, [bucket.monthKey, registerAnchor])

  return (
    <section
      ref={setAnchor}
      className="month-block"
      data-month-key={bucket.monthKey}
      data-month-anchor={bucket.monthKey}
    >
      {/* Inline header — replicates the top title so the user always sees
          which month they're browsing. The pinned top header tracks whichever
          month owns the most viewport pixels, so this inline title feels like
          it hands the baton up when scrolling past. */}
      <div className="month-block__title">
        <MonthTitle month={bucket.firstDay} size="sm" showYear={false} />
        <DecorativeWave className="month-block__title-wave" />
      </div>

      {bucket.hasAnyTasks ? (
        bucket.days.map(day => {
          // Narrow the page-wide edit/select IDs down to just this day so
          // days without the active task hit DayGroup's React.memo and skip
          // re-rendering when the user expands something elsewhere.
          const dayEditing = day.tasks.some(t => t.id === editingTaskId) ? editingTaskId : null
          const daySelected = day.tasks.some(t => t.id === selectedTaskId) ? selectedTaskId : null
          const dayIsCreating = creatingDateKey === day.dateKey
          return (
            <DayGroup
              key={day.dateKey}
              date={day.date}
              tasks={day.tasks}
              todayDateKey={todayDateKey}
              editingTaskId={dayEditing}
              selectedTaskId={daySelected}
              isCreating={dayIsCreating}
              onStartEdit={onStartEdit}
              onCancelEdit={onCancelEdit}
              onNavigateEdit={onNavigateEdit}
              onSelectTask={onSelectTask}
              onStartCreate={onStartCreate}
              onCancelCreate={onCancelCreate}
              onSubmitCreate={onSubmitCreate}
              onToggleComplete={onToggleComplete}
              onDelete={onDelete}
              onUpdateTask={onUpdateTask}
              onDuplicate={onDuplicate}
              onContextMenuBadgeUpdate={onContextMenuBadgeUpdate}
            />
          )
        })
      ) : (
        <div className="month-block__empty">
          <p className="month-block__empty-text">{t('upcoming.emptyMonth.title')}</p>
        </div>
      )}
    </section>
  )
}

export const MonthBlock = React.memo(MonthBlockComponent)
